import { Button } from '@1trading/react/components/ui/button';
import { GradientIcon } from '@1trading/react/components/ui/gradient-icon';
import { useIsMutating } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Timer } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router-dom';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import type { LdFeatureFlags } from '@/config/features';
import { useProfileInstitutionDetailsAnswers } from '@/hooks/queries/use-profile-institution-details';
import { useProfileVerificationInformation, useProfileVerificationSubmissionWait } from '@/hooks/queries/use-profile-verification-information';
import { QUERY_KEY as PUT_USER_QUERY_KEY, useUserPutMutation } from '@/hooks/queries/use-user';
import { useOnboardingCompleteRedirect } from '@/hooks/use-onboarding-complete-redirect';
import BaseLayout from '@/layouts/base';
import LoadingPage from '@/pages/loading';

const ID = 'institution-onboarding-complete-page';

const InstitutionOnboardingCompletePage = () => {
  const { isCompleteOnboarding: isCompleteInstitutionDetailsOnboarding, isLoading: isLoadingInstitutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const { isLoading: isLoadingVerificationInformation, data: verificationInformation } = useProfileVerificationInformation();
  const isCompleteVerificationInformation = !!verificationInformation?.verification && verificationInformation.verification.status !== 'NOT_VERIFIED';
  const isReadyToPutUser = !!isCompleteInstitutionDetailsOnboarding && isCompleteVerificationInformation;
  const { isWaiting: isWaitingForVerification } = useProfileVerificationSubmissionWait(isReadyToPutUser);
  const { isIdle: isIdlePutUser, mutate: putUser } = useUserPutMutation();
  const isPendingPutUser = useIsMutating({ mutationKey: PUT_USER_QUERY_KEY }) > 0;
  const { futuresEnabled, futuresPublicTrading } = useFlags<LdFeatureFlags>();
  const { t } = useTranslation();
  const hasTriggeredPutUser = useRef(false);
  const shouldPutUser = isReadyToPutUser && !isWaitingForVerification;
  const isPuttingUser = shouldPutUser && (isIdlePutUser || isPendingPutUser);
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingVerificationInformation || isWaitingForVerification || isPuttingUser;
  const isFuturesEnabledWithPublicTradingEnabled = futuresEnabled && futuresPublicTrading.enabled;

  useEffect(() => {
    if (shouldPutUser && !hasTriggeredPutUser.current) {
      hasTriggeredPutUser.current = true;

      putUser();
    }
  }, [putUser, shouldPutUser]);

  const { isRedirecting } = useOnboardingCompleteRedirect(isLoading, { followExternal: false });

  if (isLoading) return <LoadingPage />;

  if (isRedirecting)
    return (
      <LoadingPage>
        <p>{t('redirecting')}</p>
      </LoadingPage>
    );

  if (!isCompleteInstitutionDetailsOnboarding) {
    return <Navigate to="/institution/onboarding" replace />;
  }

  if (!isCompleteVerificationInformation) {
    return <Navigate to="/institution/onboarding/verification" replace />;
  }

  return (
    <BaseLayout className="-md:justify-center space-y-8" id={ID} size="sm">
      <GradientIcon className="size-32 mx-auto" strokeWidth={1.5}>
        <Timer />
      </GradientIcon>
      <PageHeading centered>
        <PageHeadingTitle>{t('institutionOnboardingProgress.complete.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionOnboardingProgress.complete.description')}</PageHeadingDescription>
        {isFuturesEnabledWithPublicTradingEnabled ? (
          <PageHeadingTitle className="!mt-6 text-lg md:text-2xl" asChild>
            <h2>{t('onboardingProgress.complete.nextSteps.title')}</h2>
          </PageHeadingTitle>
        ) : null}
      </PageHeading>
      <footer className="flex flex-col gap-4">
        {isFuturesEnabledWithPublicTradingEnabled ? (
          <Button size="lg" variant="primary" asChild>
            <Link to="/institution/futures" replace>
              {t('onboardingProgress.complete.nextSteps.futuresSignup.title')}
            </Link>
          </Button>
        ) : null}
        <Button size="lg" variant={isFuturesEnabledWithPublicTradingEnabled ? 'outline' : 'primary'} asChild>
          <a href={import.meta.env.VITE_APP_EXCHANGE_URL}>{t('buttons.exchange')}</a>
        </Button>
      </footer>
    </BaseLayout>
  );
};
InstitutionOnboardingCompletePage.displayName = 'InstitutionOnboardingCompletePage';

export default InstitutionOnboardingCompletePage;
